<script setup>
import { displayGameName } from '../../../shared/game-display-name.mjs'
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { apiConfig, authStorage, request } from '../services/api'
import { refreshAccessToken } from '../services/auth'
import { createSocketRecovery } from '../services/socket-recovery.mjs'
import SharedGameToolbar from '../components/SharedGameToolbar.vue'
import ChatFollowBetModal from '../components/ChatFollowBetModal.vue'
import { useChatFollowBet } from '../services/chat-follow-bet'

const route = useRoute()
const gameInfo = ref(null)
const gameToolbar = ref(null)
const gameState = ref(null)
const userProfile = ref(null)
const todayBill = ref(null)
const oddsTypes = ref([])
const statistic = ref({ total: [], member: [] })
const historyRows = ref([])
const activeTypeName = ref('')
const selectedPlayIds = ref(new Set())
const betAmounts = ref({})
const quickAmount = ref('')
const showBetBox = ref(false)
const showHistory = ref(false)
const showRoad = ref(true)
const roadType = ref('gyh_dx')
const loading = ref(true)
const submitting = ref(false)
const betSucceeded = ref(false)
const showBetSuccessToast = ref(false)
const notice = ref('')
const playType = ref('play')
const chatMessages = ref([])
const chatContent = ref('')
/**
 * 后台游戏列表里的「聊天室」开关（game_chat）。本页原来无条件显示聊天室 tab，
 * 后台那个开关对会员端完全不生效，这里补上。
 * 旧版依据：chunk 24/26/27/28/29/30 的模板里都有 *ngIf="game.game_chat"。
 *
 * 用 Number() 比较而不是直接判真假 —— game_chat 是 tinyint，接口若把它序列化成
 * 字符串 '0'，直接判真假会因为 '0' 是 truthy 而误判成开启。gameInfo 加载完成前
 * 按旧版构造函数的初始值 game_chat:1 处理，默认显示。
 */
const chatEnabled = computed(() => Number(gameInfo.value?.game_chat ?? 1) !== 0)
const chatNewCount = ref(0)
const chatOnline = ref(0)
const chatScroll = ref(null)
const chatShow = ref({ bet_me: true, bet_other: true, collect: true, statistic: true })
const cancelConfirmTarget = ref(null)
const now = ref(Date.now())
const stateLoadedAt = ref(Date.now())
let refreshTimer
let clockTimer
let socket
let socketRetryTimer
let socketRetryCount = 0
let successCloseTimer
let successToastTimer
let enteredAt = 0
let pageLeft = false
let socketConnecting = false
const socketRecovery = createSocketRecovery({ connect: connectLegacyGameSocket,
  refresh: () => Promise.allSettled([refresh(), reloadAccount()]), isStopped: () => pageLeft })
let lastChatTimestamp = 0

// 与 PjxBetPage 保持同一套号码配色，旧版赛车/大吃小共用这一组球色。
const palette = { 1: 'olive', 2: 'blue', 3: 'black', 4: 'orange', 5: 'teal', 6: 'purple', 7: 'gray', 8: 'red', 9: 'brown', 10: 'green' }
// 玩法后缀 -> 属性玩法标签，取自 games/xyft/XyftPlay.php 的 play_name 命名规则。
const propSuffixes = [
  ['dxd', '大'], ['dxx', '小'], ['dsd', '单'], ['dss', '双'], ['lhl', '龙'], ['lhh', '虎'],
]
const roadChoices = [
  { key: 'gyh_dx', title: '冠亚大小' },
  { key: 'gyh_ds', title: '冠亚单双' },
  { key: 'd1q_dx', title: '冠军大小' },
  { key: 'd1q_ds', title: '冠军单双' },
]

const availableBalance = computed(() => Number(userProfile.value?.balance ?? userProfile.value?.money ?? 0))
const unsettledAmount = computed(() => Number(todayBill.value?.bet_money_uncheck ?? 0))
const todayProfit = computed(() => Number(todayBill.value?.bet_money_pl ?? 0))
const currentGame = computed(() => gameInfo.value?.game_name || route.params.game)
const stateElapsed = computed(() => Math.floor((now.value - stateLoadedAt.value) / 1000))
const liveCloseTime = computed(() => Number(gameState.value?.close_time || 0) - stateElapsed.value)
const liveLotteryTime = computed(() => Number(gameState.value?.lottery_time || 0) - stateElapsed.value)
const remaining = computed(() => Math.max(0, liveCloseTime.value))
const isGameUnopened = computed(() => Number(gameState.value?.lottery_state) === -1)
const isOpen = computed(() => !isGameUnopened.value && liveCloseTime.value > 0)
const isSealed = computed(() => !isGameUnopened.value && liveCloseTime.value <= 0)
const phaseTimeLabel = computed(() => (liveCloseTime.value >= 0 ? '封盘：' : '开奖：'))
const phaseTime = computed(() => duration(liveCloseTime.value >= 0 ? liveCloseTime.value : liveLotteryTime.value))

function money(value) { return Number(value || 0).toFixed(2) }
function duration(value) {
  const seconds = Math.max(0, Number(value || 0))
  return `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`
}
function numbers(value) {
  if (Array.isArray(value)) return value.map(Number).filter((item) => item >= 1 && item <= 10)
  return (String(value || '').match(/\d{1,2}/g) || []).map(Number).filter((item) => item >= 1 && item <= 10)
}
function color(number) { return palette[Number(number)] || 'gray' }

/**
 * 本地复算开奖派生结果，公式与 games/xyft/Xyft.php::formatLotteryNumbers 完全一致：
 * 冠亚和 = 前两名之和，>=11 为大；单个号码 >=6 为大；龙虎按 1↔10、2↔9、3↔8、4↔7、5↔6 比较。
 * 仅在后端未返回 format_lottery_numbers.result 时兜底使用。
 */
function deriveResult(balls) {
  if (!Array.isArray(balls) || balls.length < 2) return {}
  const gyh = Number(balls[0]) + Number(balls[1])
  return {
    gyh,
    gyh_dx: gyh >= 11 ? '大' : '小',
    gyh_ds: gyh % 2 ? '单' : '双',
    num: balls.map(Number),
    num_dx: balls.map((ball) => (Number(ball) >= 6 ? '大' : '小')),
    num_ds: balls.map((ball) => (Number(ball) % 2 ? '单' : '双')),
    num_lh: balls.slice(0, 5).map((ball, index) => (Number(ball) > Number(balls[balls.length - index - 1]) ? '龙' : '虎')),
  }
}
function normalizeHistory(row) {
  const formatted = row.format_lottery_numbers || {}
  const balls = numbers(formatted.source || row.history_lottery_numbers || row.lottery_numbers)
  const result = formatted.result && Object.keys(formatted.result).length ? formatted.result : deriveResult(balls)
  return { no: String(row.game_no || row.history_game_no || ''), balls, result }
}
const lastLottery = computed(() => historyRows.value[0] || null)

function playKind(play) {
  const name = String(play?.play_name || '')
  const matched = propSuffixes.find(([suffix]) => name.endsWith(suffix))
  if (matched) return matched[1]
  return /\d{2}$/.test(name) ? 'number' : 'other'
}
// 赔率与玩法完全由 /home/game/odds 下发，前端不写死任何盘口数值。
const types = computed(() => (oddsTypes.value || []).map((type) => {
  const plays = (type.play || []).filter((play) => play.play_id)
  return {
    typeName: String(type.type_name || ''),
    typeTitle: type.type_title || type.type_name || '',
    numbers: plays.filter((play) => playKind(play) === 'number'),
    props: plays.filter((play) => playKind(play) !== 'number'),
  }
}).filter((type) => type.numbers.length || type.props.length))
const activeType = computed(() => types.value.find((type) => type.typeName === activeTypeName.value) || types.value[0] || null)
const allPlays = computed(() => (oddsTypes.value || []).flatMap((type) => type.play || []))

function statFor(playId, bucket = 'total') {
  const row = (statistic.value[bucket] || []).find((item) => String(item.play_id) === String(playId))
  return { money: Number(row?.bet_money || 0), win: Number(row?.bet_money_win || 0) }
}
const periodTotalBet = computed(() => (statistic.value.total || []).reduce((sum, item) => sum + Number(item.bet_money || 0), 0))

const selectedEntries = computed(() => allPlays.value
  .filter((play) => selectedPlayIds.value.has(String(play.play_id)))
  .map((play) => {
    const type = (oddsTypes.value || []).find((item) => (item.play || []).some((child) => String(child.play_id) === String(play.play_id)))
    return {
      play,
      typeTitle: type?.type_title || type?.type_name || '',
      amount: Number(betAmounts.value[String(play.play_id)] || 0),
    }
  }))
const totalBetAmount = computed(() => selectedEntries.value.reduce((sum, entry) => sum + entry.amount, 0))
const totalPotentialProfit = computed(() => selectedEntries.value.reduce((sum, entry) => sum + Math.max(0, entry.amount * (Number(entry.play.bet_odds || 0) - 1)), 0))
const canSubmit = computed(() => isOpen.value
  && selectedEntries.value.length > 0
  && selectedEntries.value.every((entry) => entry.amount > 0)
  && Boolean(gameState.value?.game_no)
  && !submitting.value
  && !betSucceeded.value)

const roadItems = computed(() => [...historyRows.value].reverse().map((row) => {
  const result = row.result || {}
  if (roadType.value === 'gyh_dx') return { label: result.gyh_dx || '', color: result.gyh_dx === '大' ? 'number-2' : 'number-1' }
  if (roadType.value === 'gyh_ds') return { label: result.gyh_ds || '', color: result.gyh_ds === '单' ? 'number-2' : 'number-1' }
  if (roadType.value === 'd1q_dx') { const value = (result.num_dx || [])[0] || ''; return { label: value, color: value === '大' ? 'number-2' : 'number-1' } }
  const value = (result.num_ds || [])[0] || ''
  return { label: value, color: value === '单' ? 'number-2' : 'number-1' }
}).filter((item) => item.label))

function togglePlay(play) {
  if (!isOpen.value || !play?.play_id) return
  const key = String(play.play_id)
  const next = new Set(selectedPlayIds.value)
  next.has(key) ? next.delete(key) : next.add(key)
  selectedPlayIds.value = next
}
function resetSelection() {
  selectedPlayIds.value = new Set()
  betAmounts.value = {}
  quickAmount.value = ''
  betSucceeded.value = false
  notice.value = ''
}
function dismissSuccessfulBet() {
  window.clearTimeout(successCloseTimer)
  showBetBox.value = false
  resetSelection()
}
function removePlay(playId) {
  const key = String(playId)
  const next = new Set(selectedPlayIds.value)
  next.delete(key)
  selectedPlayIds.value = next
  const nextAmounts = { ...betAmounts.value }
  delete nextAmounts[key]
  betAmounts.value = nextAmounts
  if (!next.size) showBetBox.value = false
}
function applyQuickAmount() {
  const value = Number(quickAmount.value || 0)
  if (!value) return
  betAmounts.value = Object.fromEntries(selectedEntries.value.map((entry) => [String(entry.play.play_id), value]))
}

function chatData(message) { return message?.data || {} }
function chatTimestamp(message) {
  const value = Number(message?.time || message?.timestamp || 0)
  return value > 1e12 ? Math.floor(value / 1000) : value
}
function chatTime(value) {
  const stamp = chatTimestamp({ time: value })
  if (!stamp) return ''
  const date = new Date(stamp * 1000)
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
}
function chatName(message) {
  const data = chatData(message)
  return data.member_nickname || data.member?.member_nickname || data.member?.nickname || data.member_username || data.member?.member_username || data.member?.username || message?.member?.member_nickname || message?.member?.nickname || message?.member?.member_username || message?.member?.username || ''
}
function chatBets(message) {
  const data = chatData(message)
  return data.lists || data.bet_lists || (Array.isArray(data) ? data : [data])
}
function isMyChatMessage(message) {
  const data = chatData(message)
  const own = [userProfile.value?.username, userProfile.value?.member_username, userProfile.value?.nickname, userProfile.value?.member_nickname].filter(Boolean).map(String)
  const sender = [data.member_username, data.member?.member_username, data.member?.username, data.member_nickname, data.member?.member_nickname, data.member?.nickname, message?.member?.member_username, message?.member?.username, message?.member?.member_nickname, message?.member?.nickname].filter(Boolean).map(String)
  return sender.some((name) => own.includes(name))
}
function chatGameNo(message) { const data = chatData(message); return data.game_no || data.game?.game_no || '' }
// 头像相对路径挂在后端站点根上，从已配置的 apiServer 推导出源站，避免再写死域名。
const assetOrigin = (() => {
  try { return new URL(apiConfig.apiServer).origin } catch (_) { return '' }
})()
function chatAvatar(message) {
  const data = chatData(message)
  const value = data.member_avatar || data.member?.member_avatar || data.member?.avatar || message?.member?.member_avatar || message?.member?.avatar || (isMyChatMessage(message) ? userProfile.value?.avatar : '')
  if (!value || value === '/assets/userface.png') return '/legacy/images/userface-default.png'
  if (/^(?:https?:|data:|blob:)/i.test(value)) return value
  return `${assetOrigin}${value.startsWith('/') ? value : `/${value}`}`
}
function onChatAvatarError(event) { event.target.onerror = null; event.target.src = '/legacy/images/userface-default.png' }
function chatStatisticName(item) { return item.member_nickname || item.member_username || item.member?.member_nickname || item.member?.nickname || item.member?.member_username || item.member?.username || '' }
function chatStatisticPage(message) { const page = chatData(message).page || {}; return page.total ? `(${page.index || 1}/${page.total})` : '' }
function chatCollectBalls(message) {
  const formatted = chatData(message).format_lottery_numbers || {}
  return numbers(formatted.source).map((ball, index) => ({ rank: index + 1, ball }))
}
function chatMessageVisible(message) {
  if (message.type === 'collect') return chatShow.value.collect
  if (message.type === 'statistic') return chatShow.value.statistic
  if (message.type === 'bet' || message.type === 'bet_batch') return isMyChatMessage(message) ? chatShow.value.bet_me : chatShow.value.bet_other
  return true
}
function toggleChatFilter(key) { chatShow.value = { ...chatShow.value, [key]: !chatShow.value[key] } }
async function scrollChatContent() { await nextTick(); if (chatScroll.value) chatScroll.value.scrollTop = chatScroll.value.scrollHeight }
function appendChatMessage(message) {
  if (!message?.type) return
  if (chatMessages.value.length >= 100) chatMessages.value.splice(0, 1)
  const stamp = chatTimestamp(message)
  if (stamp && stamp - lastChatTimestamp >= 60) chatMessages.value.push({ type: 'time', time: stamp, id: `time-${stamp}` })
  if (stamp) lastChatTimestamp = stamp
  chatMessages.value.push({ ...message, id: message.id || `${message.type}-${stamp || Date.now()}-${Math.random()}` })
  scrollChatContent()
}
function canFollowChatBet(message) { return String(chatGameNo(message)) === String(gameState.value?.game_no || '') && isOpen.value }
const { target: followTarget, busy: followBusy, error: followError, toast: followToast, validation: followValidation, open: openFollow, close: closeFollow, confirm: confirmFollow } = useChatFollowBet({
  balance: availableBalance,
  allowed: (target) => target.game === gameInfo.value?.game_id && canFollowChatBet(target.message) && !chatData(target.message).cancel,
  send: (entries, target) => request('/game/bet/bet_batch', { method: 'POST', data: {
    game: target.game, game_no: target.period,
    bet_lists: entries.map((entry) => ({ play_id: entry.playId, bet_money: Number(entry.amount), bet_content: entry.content })),
  } }),
  afterSuccess: () => Promise.all([refresh(), reloadAccount()]),
})
function followChatBetBatch(message) {
  if (!canFollowChatBet(message)) return
  openFollow({ message, player: chatName(message), period: chatGameNo(message), game: gameInfo.value?.game_id,
    entries: chatBets(message).map((bet) => {
      const play = allPlays.value.find((item) => String(item.play_id) === String(bet.play_id))
      return { playId: play?.play_id, content: play?.bet_content, label: bet.play_title || bet.play_name || play?.play_name, amount: bet.bet_money ?? bet.money ?? 0 }
    }),
  })
}
function isChatBetCancelable(message) { return isMyChatMessage(message) && String(chatGameNo(message)) === String(gameState.value?.game_no || '') && isOpen.value && !chatData(message).cancel }
function requestChatCancel(message) { if (isChatBetCancelable(message)) cancelConfirmTarget.value = message }
async function confirmChatCancel() {
  const message = cancelConfirmTarget.value
  cancelConfirmTarget.value = null
  if (!message) return
  try {
    const bets = chatBets(message)
    const results = await Promise.all(bets.map((bet) => request('/game/bet/cancel', { method: 'POST', data: { bet_id: bet.bet_id } })))
    if (results.every((item) => Number(item.error_code || 0) === 0)) {
      chatData(message).cancel = true
      bets.forEach((bet) => { bet.cancel = true })
      await refresh()
    }
  } catch (_) { /* 撤单被拒绝时保留原消息，交由用户重试 */ }
}
function selectChatBlock(type) {
  playType.value = type
  if (type === 'chat') { chatNewCount.value = 0; scrollChatContent() }
}
function sendChatMessage() {
  const message = chatContent.value.trim()
  if (!message || !socket || socket.readyState !== WebSocket.OPEN) return
  socket.send(JSON.stringify({ type: 'chat', game: gameInfo.value?.game_id || route.params.game, message }))
  chatContent.value = ''
}

/**
 * 连接旧版同一个 WebSocket 服务。握手帧与 application/service/controller/Swoole.php
 * 的 onMessage('connect') 约定一致：client 必须是 web-user，token 用 access_token。
 * 连接前先刷新令牌，避免 token 临期导致 1001 静默失败。
 */
async function connectLegacyGameSocket() {
  if (pageLeft || socketConnecting || (socket && socket.readyState <= 1)) return
  const gameId = gameInfo.value?.game_id
  if (!gameId || !apiConfig.wsServer) return
  let token = authStorage.getAccessToken()
  socketConnecting = true
  try { token = (await refreshAccessToken()) || token } catch (_) { /* 刷新失败时仍尝试用现有令牌连接 */ }
  finally { socketConnecting = false }
  if (!token || pageLeft) return
  const reconnect = socketRetryCount > 0 ? 1 : 0
  try { socket = new WebSocket(apiConfig.wsServer) } catch (_) { socketRecovery.retry(); return }
  const activeSocket = socket
  socket.binaryType = 'arraybuffer'
  socket.onopen = () => {
    if (socket !== activeSocket || pageLeft) return
    if (reconnect) enteredAt = Math.floor(Date.now() / 1000)
    socketRetryCount = 0
    socket?.send(JSON.stringify({ type: 'connect', game: gameId, client: 'web-user', token, reconnect }))
    socketRecovery.opened(Boolean(reconnect))
  }
  socket.onmessage = (message) => {
    try {
      const response = JSON.parse(message.data)
      const event = response?.error_code === 0 ? response.data : null
      if (!event?.type) return
      if (event.type === 'connect' || event.type === 'online' || event.type === 'close') {
        chatOnline.value = Number(chatData(event).count || 0)
        return
      }
      if (event.type === 'collect') {
        // 旧版 web/24：仅实时 collect 进入 setLottery 并播放开奖声音。
        if (parseInt(event.time, 10) > enteredAt && event.data) {
          void refresh()
          gameToolbar.value?.playLottery()
        }
        appendChatMessage(event)
        return
      }
      if (event.type === 'bet_cancel') {
        const cancelled = chatData(event).bet_id
        chatMessages.value.forEach((item) => chatBets(item).forEach((bet) => {
          if (String(bet.bet_id) === String(cancelled)) { bet.cancel = true; chatData(item).cancel = true }
        }))
        void loadStatistic()
        return
      }
      if (event.type === 'recharge_handle' || event.type === 'cash_handle' || event.type === 'cash_cancel') {
        void reloadAccount()
        return
      }
      if (['bet', 'bet_batch', 'chat', 'statistic'].includes(event.type)) {
        appendChatMessage(event)
        if (event.type === 'bet' || event.type === 'bet_batch') void loadStatistic()
        if (event.type === 'statistic') void reloadAccount()
        if (playType.value !== 'chat') chatNewCount.value += 1
      }
    } catch (_) { /* 单条畸形消息不应中断整个连接 */ }
  }
  socket.onclose = (event) => {
    if (socket !== activeSocket) return
    socket = undefined
    socketRetryCount += 1
    socketRecovery.retry(event)
  }
  socket.onerror = () => { try { activeSocket.close() } catch (_) {} }
}

async function loadState() {
  const response = await request('/game/state', { method: 'POST', data: { game: currentGame.value } })
  gameState.value = response.data || null
  stateLoadedAt.value = Date.now()
}
async function loadStatistic() {
  if (!gameState.value?.game_no) return
  const response = await request('/game/statistic', { method: 'POST', data: { game: currentGame.value, game_no: gameState.value.game_no, member: 1 } })
  statistic.value = { total: response.data?.total || [], member: response.data?.member || [] }
}
async function loadHistory() {
  const response = await request('/game/history', { method: 'POST', data: { game: currentGame.value, pagesize: gameInfo.value?.game_history_count || 25 } })
  const rows = response.data?.lists || response.data?.history || response.data || []
  historyRows.value = Array.isArray(rows) ? rows.map(normalizeHistory).filter((item) => item.no && item.balls.length) : []
}
async function reloadAccount() {
  try {
    const [profile, bill] = await Promise.all([
      request('/user/profile'),
      request('/game/bet/today', { method: 'POST', data: { game: currentGame.value } }),
    ])
    userProfile.value = profile.data || userProfile.value
    todayBill.value = bill.data || todayBill.value
  } catch (_) { /* 保留上一次成功读取的账户数据 */ }
}
async function loadAll() {
  const list = await request('/game/lists')
  gameInfo.value = (list.data?.lists || []).find((item) => String(item.game_id) === String(route.params.game)) || null
  const game = gameInfo.value?.game_name || route.params.game
  const [state, oddsResult, profile, bill, history] = await Promise.all([
    request('/game/state', { method: 'POST', data: { game } }),
    request('/game/odds', { method: 'POST', data: { game } }),
    request('/user/profile'),
    request('/game/bet/today', { method: 'POST', data: { game } }),
    request('/game/history', { method: 'POST', data: { game, pagesize: gameInfo.value?.game_history_count || 25 } }),
  ])
  gameState.value = state.data || null
  stateLoadedAt.value = Date.now()
  oddsTypes.value = oddsResult.data || []
  if (!activeTypeName.value) activeTypeName.value = types.value[0]?.typeName || ''
  userProfile.value = profile.data || null
  todayBill.value = bill.data || null
  const rows = history.data?.lists || history.data?.history || history.data || []
  historyRows.value = Array.isArray(rows) ? rows.map(normalizeHistory).filter((item) => item.no && item.balls.length) : []
  await loadStatistic()
}
async function refresh() {
  try { await loadState(); await Promise.all([loadStatistic(), loadHistory()]) } catch (_) { /* 保留上一次成功的盘面数据 */ }
}
async function submit() {
  if (!canSubmit.value) return
  submitting.value = true
  notice.value = ''
  try {
    await request('/game/bet/bet_batch', {
      method: 'POST',
      data: {
        game: gameInfo.value?.game_id,
        game_no: gameState.value.game_no,
        bet_lists: selectedEntries.value.map((entry) => ({
          play_id: entry.play.play_id,
          bet_money: entry.amount,
          bet_content: entry.play.bet_content,
        })),
      },
    })
    betSucceeded.value = true
    showBetSuccessToast.value = true
    window.clearTimeout(successCloseTimer)
    window.clearTimeout(successToastTimer)
    successCloseTimer = window.setTimeout(dismissSuccessfulBet, 2000)
    successToastTimer = window.setTimeout(() => { showBetSuccessToast.value = false }, 3500)
    await Promise.all([refresh(), reloadAccount()])
  } catch (error) {
    notice.value = error.message || '投注失败'
  } finally {
    submitting.value = false
  }
}

onMounted(async () => {
  enteredAt = Math.floor(Date.now() / 1000)
  try { await loadAll() } catch (error) { notice.value = error.message || '加载失败' } finally { loading.value = false }
  void connectLegacyGameSocket()
  clockTimer = window.setInterval(() => { now.value = Date.now() }, 1000)
  refreshTimer = window.setInterval(refresh, 10000)
})
onUnmounted(() => {
  socketRecovery.dispose()
  pageLeft = true
  window.clearInterval(clockTimer)
  window.clearInterval(refreshTimer)
  window.clearTimeout(socketRetryTimer)
  window.clearTimeout(successCloseTimer)
  window.clearTimeout(successToastTimer)
  socket?.close(3001, 'exit')
})
</script>

<template>
  <ChatFollowBetModal :target="followTarget" :balance="availableBalance" :busy="followBusy" :error="followError" :validation="followValidation" :toast="followToast" @close="closeFollow" @confirm="confirmFollow" />
  <main class="saiche-page">
    <SharedGameToolbar
      ref="gameToolbar"
      :game-info="gameInfo"
      :draw-guard="gameState?.draw_guard"
      :user-profile="userProfile"
      :available-balance="availableBalance"
    />

    <nav class="saiche-tabs">
      <button type="button" :class="{ active: playType === 'play' }" @click="selectChatBlock('play')">下注</button>
      <!-- v-if="chatEnabled"：后台「聊天室」开关关掉时不显示这个 tab（旧版 *ngIf="game.game_chat"） -->
      <button v-if="chatEnabled" type="button" :class="{ active: playType === 'chat' }" @click="selectChatBlock('chat')">聊天室<b v-if="chatNewCount">{{ chatNewCount }}</b></button>
    </nav>

    <section class="account-strip">
      <span>可用金额：<b>{{ money(availableBalance) }}</b></span>
      <span>未结金额：<em>{{ money(unsettledAmount) }}</em></span>
      <span>今日输赢：<em>{{ money(todayProfit) }}</em></span>
    </section>

    <!-- || !chatEnabled 是兜底：聊天室开关被关掉时，即便 playType 还停在 'chat' 也要显示盘面 -->
    <div v-if="playType === 'play' || !chatEnabled" class="saiche-scroll">
      <section class="last-area">
        <div class="last-title">
          <span>{{ lastLottery?.no || '—' }} 期</span>
          <span v-if="lastLottery">冠亚和 <b>{{ lastLottery.result?.gyh ?? '-' }}</b> {{ lastLottery.result?.gyh_dx || '' }}{{ lastLottery.result?.gyh_ds || '' }}</span>
          <button type="button" aria-label="开奖历史" @click="showHistory = !showHistory">{{ showHistory ? '收起' : '历史' }}</button>
        </div>
        <div class="last-numbers">
          <span v-for="(ball, index) in (lastLottery?.balls || [])" :key="`${ball}-${index}`" class="last-slot">
            <i :class="['lottery-ball', color(ball)]">{{ String(ball).padStart(2, '0') }}</i>
            <small>{{ index + 1 }}</small>
          </span>
          <p v-if="!lastLottery" class="last-empty">暂无开奖记录</p>
        </div>
      </section>

      <section v-if="showHistory" class="history-panel">
        <article v-for="item in historyRows" :key="item.no">
          <span>{{ item.no }}</span>
          <div><i v-for="(ball, index) in item.balls" :key="`${item.no}-${index}`" :class="color(ball)">{{ String(ball).padStart(2, '0') }}</i></div>
          <b>{{ item.result?.gyh ?? '-' }}</b>
        </article>
        <p v-if="!historyRows.length" class="panel-empty">暂无开奖记录</p>
      </section>

      <section v-else class="road-area">
        <div class="road-type-list">
          <button
            v-for="choice in roadChoices"
            :key="choice.key"
            type="button"
            :class="['road-type-button', { 'road-type-active': roadType === choice.key }]"
            @click="roadType = choice.key"
          >{{ choice.title }}</button>
          <button type="button" class="road-type-button" @click="showRoad = !showRoad">{{ showRoad ? '隐藏' : '显示' }}</button>
        </div>
        <div v-if="showRoad" class="road-list">
          <i v-for="(item, index) in roadItems" :key="index" :class="['road-ball', item.color]">{{ item.label }}</i>
          <p v-if="!roadItems.length" class="panel-empty">暂无路子数据</p>
        </div>
      </section>

      <section class="period-bar">
        <span>{{ gameState?.game_no || '未开盘' }} 期</span>
        <b>总投注：<em>{{ money(periodTotalBet) }}</em></b>
        <span>{{ phaseTimeLabel }}{{ phaseTime }} <button type="button" aria-label="刷新" @click="refresh">↻</button></span>
      </section>

      <nav class="bet-type-lists">
        <button
          v-for="type in types"
          :key="type.typeName"
          type="button"
          :class="['bet-type-item', { 'bet-type-active': type.typeName === activeType?.typeName }]"
          @click="activeTypeName = type.typeName"
        >{{ displayGameName(type.typeTitle) }}</button>
      </nav>

      <section v-if="activeType" :class="['bet-box', { 'is-sealed': isSealed }]">
        <div v-if="activeType.numbers.length" :class="['bet-grid', activeType.typeName === 'gyh' ? 'grid-6' : 'grid-5']">
          <button
            v-for="play in activeType.numbers"
            :key="play.play_id"
            type="button"
            :class="['bet-box-item', { selected: selectedPlayIds.has(String(play.play_id)) }]"
            @click="togglePlay(play)"
          >
            <strong>{{ play.play_title || play.play_name }}</strong>
            <small class="bet-item-odds">{{ Number(play.bet_odds || 0).toFixed(3) }}</small>
            <em>{{ money(statFor(play.play_id).money) }}</em>
          </button>
        </div>
        <div v-if="activeType.props.length" class="bet-grid grid-props">
          <button
            v-for="play in activeType.props"
            :key="play.play_id"
            type="button"
            :class="['bet-box-item', 'prop-item', { selected: selectedPlayIds.has(String(play.play_id)) }]"
            @click="togglePlay(play)"
          >
            <strong>{{ play.play_title || play.play_name }}</strong>
            <small class="bet-item-odds">{{ Number(play.bet_odds || 0).toFixed(3) }}</small>
            <em>{{ money(statFor(play.play_id).money) }}</em>
          </button>
        </div>
      </section>

      <p v-if="isGameUnopened" class="panel-empty">当前游戏未开盘</p>
      <p v-else-if="loading" class="panel-empty">加载中…</p>
      <p v-else-if="!types.length" class="panel-empty">暂未获取到玩法与赔率</p>
    </div>

    <footer v-if="selectedPlayIds.size && playType === 'play'" class="bet-action">
      <button type="button" @click="showBetBox = true">立即下注</button>
      <button type="button" class="reset" @click="resetSelection">重 置</button>
    </footer>

    <section v-if="showBetBox" class="bet-confirm-mask" @click.self="!betSucceeded && (showBetBox = false)">
      <div class="bet-confirm-dialog">
        <header>
          <span>可用：<b>{{ money(availableBalance) }}</b></span>
          <span>{{ duration(remaining) }}</span>
          <button type="button" aria-label="关闭" @click="dismissSuccessfulBet">×</button>
        </header>
        <div class="confirm-quick">
          <span>快速金额：</span>
          <input v-model="quickAmount" :disabled="betSucceeded" inputmode="decimal" placeholder="快速金额" @change="applyQuickAmount" @keyup.enter="applyQuickAmount">
        </div>
        <div class="confirm-head"><b>玩法</b><b>金额</b><b>可赢</b><b>状态</b></div>
        <div v-for="entry in selectedEntries" :key="entry.play.play_id" class="confirm-row">
          <span>{{ displayGameName(entry.typeTitle) }} {{ entry.play.play_title || entry.play.play_name }}</span>
          <input v-model="betAmounts[String(entry.play.play_id)]" :disabled="betSucceeded" inputmode="decimal" placeholder="0">
          <span>{{ money(Math.max(0, entry.amount * (Number(entry.play.bet_odds || 0) - 1))) }}</span>
          <button v-if="!betSucceeded" type="button" :aria-label="`删除 ${entry.play.play_title || entry.play.play_name}`" @click="removePlay(entry.play.play_id)">×</button>
          <span v-else class="confirm-success-check" aria-label="投注成功">✓</span>
        </div>
        <p v-if="notice" class="confirm-error">{{ notice }}</p>
        <div class="confirm-summary">
          <span>投注数：<b>{{ selectedEntries.length }}</b></span>
          <span>总金额：<b>{{ money(totalBetAmount) }}</b></span>
          <span>可盈利：<b>{{ money(totalPotentialProfit) }}</b></span>
        </div>
        <button
          type="button"
          :class="['confirm-submit', { success: betSucceeded }]"
          :disabled="!betSucceeded && !canSubmit"
          @click.prevent="betSucceeded ? dismissSuccessfulBet() : submit()"
        >{{ betSucceeded ? '下注成功，请确认' : (submitting ? '投注中…' : '确定下注') }}</button>
      </div>
    </section>

    <div v-if="showBetSuccessToast" class="bet-success-toast" role="status">投注成功</div>
    <p v-if="notice && !showBetBox && playType === 'play'" class="page-notice">{{ notice }}</p>

    <section v-if="chatEnabled && playType === 'chat'" class="saiche-chat-content">
      <div ref="chatScroll" class="saiche-chat-container">
        <article v-for="message in chatMessages" v-show="chatMessageVisible(message)" :key="message.id" :class="['saiche-msg-item', `saiche-msg-${message.type}`]">
          <p v-if="message.type === 'time'" class="saiche-msg-time">{{ chatTime(message.time) }}</p>
          <template v-else-if="message.type === 'chat'">
            <div :class="['saiche-chat-message', isMyChatMessage(message) ? 'saiche-chat-me' : 'saiche-chat-other']">
              <img class="saiche-chat-avatar" :src="chatAvatar(message)" alt="" @error="onChatAvatarError">
              <div class="saiche-chat-msg-content">
                <p class="saiche-chat-nickname">{{ chatName(message) }}</p>
                <p class="saiche-chat-msg-text">{{ chatData(message).message || chatData(message).content }}</p>
              </div>
            </div>
          </template>
          <template v-else-if="message.type === 'bet' || message.type === 'bet_batch'">
            <div :class="['saiche-chat-message', isMyChatMessage(message) ? 'saiche-chat-me' : 'saiche-chat-other']">
              <img class="saiche-chat-avatar" :src="chatAvatar(message)" alt="" @error="onChatAvatarError">
              <div class="saiche-chat-msg-content">
                <p class="saiche-chat-nickname">{{ chatName(message) }}</p>
                <div class="saiche-bet-msg">
                  <p>{{ isMyChatMessage(message) ? '我刚刚投了' : '刚刚投了' }} <b>{{ chatGameNo(message) }}</b> 期 {{ chatBets(message).length }} 注</p>
                  <div class="saiche-bet-msg-detail">
                    <div class="saiche-bet-lines">
                      <div v-for="bet in chatBets(message)" :key="bet.bet_id || bet.play_id" class="saiche-bet-line">
                        <span>{{ bet.play_title || bet.play_name || bet.bet_content }} 投了：<b>{{ money(bet.bet_money || bet.money) }}</b></span>
                      </div>
                    </div>
                    <div class="saiche-bet-action">
                      <button v-if="isMyChatMessage(message)" type="button" :disabled="!isChatBetCancelable(message) || chatData(message).cancel" @click="requestChatCancel(message)">{{ chatData(message).cancel ? '已取消' : '取消' }}</button>
                      <button v-else type="button" :disabled="!canFollowChatBet(message) || chatData(message).cancel" @click="followChatBetBatch(message)">{{ chatData(message).cancel ? '已取消' : '跟投' }}</button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>
          <section v-else-if="message.type === 'collect'" class="saiche-msg-card saiche-collect-card">
            <strong>第 {{ chatData(message).game_no }} 期开奖公告</strong>
            <div class="saiche-collect-balls">
              <span v-for="item in chatCollectBalls(message)" :key="item.rank"><i>{{ item.rank }}</i><b :class="color(item.ball)">{{ String(item.ball || '').padStart(2, '0') }}</b></span>
            </div>
          </section>
          <section v-else-if="message.type === 'statistic'" class="saiche-msg-card saiche-statistic-card">
            <!-- 期号取 chatGameNo：statistic 消息的期号在 data.game.game_no，
                 不在 data.game_no（见 common/logic/Game.php:222 的推送结构）。 -->
            <strong>第 {{ chatGameNo(message) }} 期开奖报表{{ chatStatisticPage(message) }}</strong>
            <div class="saiche-stat-head"><b>昵称</b><b>下注</b><b>中奖</b><b>盈利</b></div>
            <div v-for="item in chatData(message).lists || []" :key="item.member_id || chatStatisticName(item)" class="saiche-stat-row">
              <span>{{ chatStatisticName(item) }}</span>
              <span class="saiche-stat-money">{{ money(item.bet_money) }}</span>
              <span class="saiche-stat-money">{{ money(item.bet_money_win ?? item.win_money) }}</span>
              <span :class="Number(item.bet_money_pl ?? item.pl_money) < 0 ? 'saiche-stat-loss' : 'saiche-stat-money'">{{ money(item.bet_money_pl ?? item.pl_money) }}</span>
            </div>
          </section>
        </article>
      </div>
      <footer class="saiche-chat-footer">
        <div class="saiche-chat-filters">
          <button :class="{ checked: chatShow.bet_me }" @click="toggleChatFilter('bet_me')">我的投注</button>
          <button :class="{ checked: chatShow.bet_other }" @click="toggleChatFilter('bet_other')">他人投注</button>
          <button :class="{ checked: chatShow.collect }" @click="toggleChatFilter('collect')">开奖</button>
          <button :class="{ checked: chatShow.statistic }" @click="toggleChatFilter('statistic')">报表</button>
          <span v-if="chatOnline" class="saiche-chat-online">在线 {{ chatOnline }}</span>
        </div>
        <div class="saiche-chat-send">
          <input v-model="chatContent" type="text" maxlength="1000" placeholder="请输入您要发送的内容" @keyup.enter="sendChatMessage">
          <button type="button" :disabled="!chatContent.trim()" @click="sendChatMessage">发送</button>
        </div>
      </footer>
    </section>

    <section v-if="cancelConfirmTarget" class="saiche-cancel-mask">
      <div class="saiche-cancel-dialog">
        <h2>请确认</h2>
        <p>您确认取消吗？</p>
        <footer>
          <button type="button" @click="cancelConfirmTarget = null">关闭</button>
          <button type="button" @click="confirmChatCancel">确认</button>
        </footer>
      </div>
    </section>
  </main>
</template>

<style scoped>
.saiche-page{display:flex;height:100vh;flex-direction:column;overflow:hidden;background:#f4f4f4;color:#111;font-family:Arial,"Microsoft YaHei",sans-serif;font-size:12px}
.saiche-tabs{display:grid;grid-template-columns:1fr 1fr;height:44px;border-bottom:1px solid #ddd;background:#fff}
.saiche-tabs button{position:relative;border:0;background:#fff;color:#222;font-size:14px}
.saiche-tabs .active{color:#ff545b}
.saiche-tabs .active::after{position:absolute;right:35%;bottom:-1px;left:35%;height:2px;background:#ff545b;content:''}
.saiche-tabs b{display:inline-grid;width:16px;height:19px;margin-left:4px;place-items:center;border-radius:4px;background:#f2414a;color:#fff;font-size:12px}
.account-strip{display:flex;justify-content:space-between;padding:5px 12px;background:#d9f8fc;color:#333}
.account-strip b{color:#f04141;font-weight:400}
.account-strip em{color:#777;font-style:normal}
.saiche-scroll{min-height:0;flex:1;overflow-x:hidden;overflow-y:scroll;scrollbar-gutter:stable;padding-bottom:60px}

.last-area{background:#fff;border-bottom:5px solid #f1f1f1}
.last-title{display:flex;height:34px;align-items:center;justify-content:space-between;padding:0 12px;border-bottom:1px solid #e5e5e5;color:#e8353d;font-size:13px}
.last-title b{color:#111;font-weight:600}
.last-title button{border:0;background:transparent;color:#888;font-size:12px}
.last-numbers{display:flex;justify-content:space-between;padding:9px 12px}
.last-slot{display:grid;justify-items:center;gap:2px}
.last-slot small{color:#999;font-size:11px}
.lottery-ball{display:inline-grid;width:28px;height:29px;place-items:center;border-radius:5px;color:#fff;font-size:15px;font-style:normal}
.last-empty{margin:6px auto;color:#999}
.olive{background:#d8dc00}.blue{background:#008ed4}.black{background:#343434}.orange{background:#f06a00}.teal{background:#0ccbd1}.purple{background:#4c2ee8}.gray{background:#b5b7b7}.red{background:#ef2b16}.brown{background:#850a03}.green{background:#00a90d}

.history-panel{background:#fff;border-bottom:5px solid #f1f1f1}
.history-panel article{display:grid;grid-template-columns:74px 1fr 32px;min-height:32px;align-items:center;padding:0 11px;border-bottom:1px solid #e5e5e5}
.history-panel article>div{display:flex;justify-content:space-between;gap:3px}
.history-panel i{display:inline-grid;width:23px;height:23px;place-items:center;border-radius:4px;color:#fff;font-size:11px;font-style:normal}
.history-panel b{color:#e6373d;text-align:right;font-weight:600}
.panel-empty{margin:14px;color:#999;text-align:center}

.road-area{background:#fff;border-bottom:5px solid #f1f1f1}
.road-type-list{display:flex;gap:6px;padding:7px 12px;border-bottom:1px solid #eee}
.road-type-button{border:1px solid #ddd;border-radius:2px;background:#fff;color:#666;padding:3px 8px;font-size:11px}
.road-type-active{border-color:#f04141;background:#fdecec;color:#f04141}
.road-list{display:flex;flex-wrap:wrap;gap:4px;padding:9px 12px}
.road-ball{display:inline-grid;width:22px;height:22px;place-items:center;border-radius:50%;color:#fff;font-size:11px;font-style:normal}
.number-1{background:#3f9ae5}.number-2{background:#e8474e}.number-3{background:#41b883}.number-4{background:#f0a029}

.period-bar{display:grid;grid-template-columns:1fr 1fr 1fr;height:37px;align-items:center;padding:0 12px;background:#fff;color:#444}
.period-bar b{text-align:center;font-weight:400}
.period-bar em{color:#e6373d;font-size:15px;font-style:normal}
.period-bar>span:last-child{text-align:right}
.period-bar button{border:0;background:transparent;color:#c93038;font-size:15px}

.bet-type-lists{display:flex;overflow-x:auto;gap:0;border-top:1px solid #e5e5e5;border-bottom:1px solid #e5e5e5;background:#fff}
.bet-type-item{flex:0 0 auto;border:0;border-right:1px solid #eee;background:#fff;color:#444;padding:9px 14px;font-size:13px;white-space:nowrap}
.bet-type-active{background:#f04141;color:#fff}

.bet-box{position:relative;padding:12px;background:#389b61}
.bet-grid{display:grid;gap:9px}
.grid-5{grid-template-columns:repeat(5,1fr)}
.grid-6{grid-template-columns:repeat(6,1fr)}
.grid-props{grid-template-columns:repeat(6,1fr);margin-top:9px}
.bet-box-item{display:grid;min-height:52px;place-content:center;gap:2px;border:1px solid #77c697;background:#389b61;color:#fff;padding:4px 2px}
.bet-box-item strong{font-size:15px;font-weight:600}
.bet-item-odds{color:#ffe561;font-size:11px}
.bet-box-item em{color:#d5efdf;font-size:10px;font-style:normal}
.bet-box-item.selected{border-color:#ffe372;background:#24804e;box-shadow:inset 0 0 0 1px #ffe372}
.prop-item strong{font-size:14px}
.bet-box.is-sealed .bet-box-item{pointer-events:none}
.bet-box.is-sealed::after{position:absolute;inset:0;z-index:3;background:rgb(0 0 0 / 48%);content:''}

.bet-action{position:fixed;right:0;bottom:0;left:0;z-index:20;display:flex;max-width:640px;margin:auto;gap:10px;padding:8px 15px 16px;background:#fff}
.bet-action button{height:39px;flex:1;border:0;border-radius:2px;background:#f04141;color:#fff}
.bet-action .reset{flex:0 0 80px;background:#51cdcc}

.bet-confirm-mask{position:fixed;inset:0;z-index:50;display:flex;align-items:center;justify-content:center;padding:14px;background:rgb(0 0 0 / 42%)}
.bet-confirm-dialog{box-sizing:border-box;width:min(100%,480px);max-height:88vh;overflow-y:auto;padding:0 15px 14px;background:#fff;color:#333;font-size:13px}
.bet-confirm-dialog header{display:grid;grid-template-columns:1fr auto 1fr;min-height:40px;align-items:center;border-bottom:1px solid #eee}
.bet-confirm-dialog header b,.confirm-summary b{color:#f04141;font-weight:400}
.bet-confirm-dialog header button{justify-self:end;border:0;background:transparent;color:#ff545b;font-size:30px;font-weight:200;line-height:1}
.confirm-quick{display:flex;height:42px;align-items:center;justify-content:center;gap:8px}
.confirm-quick input{box-sizing:border-box;width:88px;height:30px;border:0;background:#f5f5f5;padding:0 8px;text-align:center;outline:none}
.confirm-head,.confirm-row{display:grid;grid-template-columns:1.5fr .8fr 1fr .7fr;align-items:center;text-align:center}
.confirm-head{height:34px;border-top:1px solid #eee;border-bottom:1px solid #eee}
.confirm-head b{font-weight:600}
.confirm-row{min-height:43px;border-bottom:1px solid #eee;color:#666}
.confirm-row input{box-sizing:border-box;width:80px;height:30px;justify-self:center;border:0;background:#f5f5f5;text-align:center;outline:none}
.confirm-row input:disabled,.confirm-quick input:disabled{color:#999}
.confirm-row button{border:0;background:transparent;color:#ff545b;font-size:28px;font-weight:200;line-height:1}
.confirm-success-check{justify-self:center;color:green;font-size:27px;font-weight:500;line-height:1}
.confirm-summary{display:flex;justify-content:space-between;padding:12px 0}
.confirm-error{margin:8px 0 0;color:#d92424;text-align:center}
.confirm-submit{width:100%;height:41px;border:0;border-radius:2px;background:#f04141;color:#fff;font-size:14px}
.confirm-submit:disabled{background:#ccc;color:#fff}
.confirm-submit.success{background:green}
.bet-success-toast{position:fixed;right:8px;bottom:8px;left:8px;z-index:60;display:flex;min-height:48px;align-items:center;border-radius:2px;padding:0 12px;background:rgb(30 30 30 / 92%);color:#fff;font-size:14px}
.page-notice{margin:10px;color:#d32f2f;text-align:center}

.saiche-chat-content{display:flex;min-height:0;flex:1;flex-direction:column;background:#f5f5f5}
.saiche-chat-container{flex:1;overflow-y:auto;padding:0 15px}
.saiche-msg-item{margin:8px 0}
.saiche-msg-time{color:#ccc;text-align:center;font-size:11px}
.saiche-chat-message{display:flex;align-items:flex-start}
.saiche-chat-me{justify-content:flex-end}
.saiche-chat-other{justify-content:flex-start}
.saiche-chat-avatar{width:32px;height:32px;flex:0 0 32px;object-fit:cover}
.saiche-chat-me .saiche-chat-avatar{order:2}
.saiche-chat-msg-content{position:relative;max-width:82%;margin:0 8px}
.saiche-chat-nickname{margin:0 0 2px;color:#666}
.saiche-chat-me .saiche-chat-nickname{text-align:right}
.saiche-chat-msg-text,.saiche-bet-msg{margin:0;border-radius:3px;background:#fff;color:#111}
.saiche-chat-msg-text{padding:7px 12px;line-height:18px}
.saiche-chat-me .saiche-chat-msg-text,.saiche-chat-me .saiche-bet-msg{background:#9eea6a}
.saiche-bet-msg>p{margin:0;padding:6px 12px;border-bottom:1px solid #e5e5e5}
.saiche-bet-msg-detail{display:flex;align-items:center}
.saiche-bet-lines{min-width:0;flex:1}
.saiche-bet-line{display:flex;align-items:center;gap:8px;padding:6px 12px}
.saiche-bet-line span{flex:1}
.saiche-bet-line b,.saiche-collect-card b{color:#f04141}
.saiche-bet-action{flex:0 0 auto;padding:0 12px}
.saiche-bet-action button{border:0;border-radius:2px;background:#f04141;color:#fff;padding:5px 11px}
.saiche-bet-action button:disabled{background:#ccc}
.saiche-msg-card{box-sizing:border-box;width:86%;margin:auto;border:1px solid #e5e5e5;border-radius:2px;background:#2fbc8b;color:#fff;text-align:center}
.saiche-msg-card strong{display:block;padding:6px;border-bottom:1px solid #e5e5e5}
.saiche-collect-balls{display:grid;grid-template-columns:repeat(10,1fr);gap:3px;padding:6px}
.saiche-collect-balls span{display:grid;justify-items:center;gap:4px}
.saiche-collect-balls i{font-style:normal}
.saiche-collect-balls b{display:grid;width:22px;height:22px;place-items:center;border-radius:2px;color:#fff}
.saiche-stat-head,.saiche-stat-row{display:grid;grid-template-columns:1.2fr 1fr 1fr 1fr;padding:4px 9px;line-height:18px}
.saiche-stat-money{color:#f04141}
.saiche-stat-loss{color:#fff100}
.saiche-chat-footer{background:#fff}
.saiche-chat-filters{display:flex;align-items:center;justify-content:space-around;padding:5px;background:#f5f5f5}
.saiche-chat-filters button{border:1px dashed #ccc;background:transparent;color:#999;padding:2px 5px;font-size:11px}
.saiche-chat-filters button.checked{border-color:#f04141;color:#f04141}
.saiche-chat-online{color:#999;font-size:11px}
.saiche-chat-send{display:flex;gap:8px;padding:12px 15px}
.saiche-chat-send input{min-width:0;flex:1;border:0;border-radius:3px;background:#f5f5f5;padding:8px}
.saiche-chat-send button{border:0;border-radius:2px;background:#f04141;color:#fff;padding:0 16px}
.saiche-chat-send button:disabled{background:#ccc}
.saiche-cancel-mask{position:fixed;inset:0;z-index:80;display:grid;place-items:center;background:rgb(0 0 0 / 33%)}
.saiche-cancel-dialog{width:250px;background:#fff}
.saiche-cancel-dialog h2{margin:0;padding:18px 22px 10px;font-size:18px}
.saiche-cancel-dialog p{margin:0;padding:0 22px 25px;color:#888}
.saiche-cancel-dialog footer{display:flex;justify-content:flex-end;gap:24px;padding:10px 22px 16px}
.saiche-cancel-dialog button{border:0;background:transparent;color:#f04141}

@media (min-width:641px){.saiche-page{max-width:640px;margin:auto;box-shadow:0 0 20px rgb(0 0 0 / 13%)}}
</style>
